import { useEffect, useRef } from 'react';
import { toast as sonnerToast } from 'sonner';
import { useUserStore } from '@/store/userStore';
import { buildDraftKey, useFormDraftStore } from '@/store/formDraftStore';

export interface DraftResumeEntry {
  /** Stem complet de la clé, `:new` inclus le cas échéant (ex. `course-form:new`). */
  formId: string;
  message: string;
  /** Sous-titre optionnel dérivé du brouillon (ex. « Prénom Nom »). */
  description?: (data: Record<string, unknown>) => string | undefined;
  /** Un brouillon « vide » n'est pas proposé. */
  isEmpty?: (data: Record<string, unknown>) => boolean;
  /** Rouvre le formulaire — il se restaure ensuite tout seul. */
  onResume: () => void;
}

/**
 * Affordance « Reprendre » : au montage d'une page liste, si un brouillon de
 * CRÉATION non périmé existe, propose une fois (toast sonner, 12 s) de rouvrir
 * le formulaire. Un seul toast par montage, premier brouillon trouvé.
 * Réservé aux créations — les brouillons d'édition se restaurent en silence à
 * la réouverture de leur entité (règle R2 du plan brouillons).
 */
export function useDraftResumeToast(entries: DraftResumeEntry[]) {
  const gymId = useUserStore((s) => s.gymId);
  const shownRef = useRef(false);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  useEffect(() => {
    if (shownRef.current || !gymId) return;
    const userId = useUserStore.getState().user?.id ?? null;
    for (const entry of entriesRef.current) {
      const draft = useFormDraftStore
        .getState()
        .getDraft(buildDraftKey(entry.formId, gymId, userId));
      const data = draft?.data as Record<string, unknown> | undefined;
      if (!data || typeof data !== 'object') continue;
      if (entry.isEmpty && entry.isEmpty(data)) continue;
      shownRef.current = true;
      sonnerToast.info(entry.message, {
        description: entry.description?.(data),
        duration: 12000,
        action: {
          label: 'Reprendre',
          onClick: entry.onResume,
        },
      });
      return;
    }
  }, [gymId]);
}
